import React, { useState, useContext, useEffect } from"react";
import { useNavigate } from"react-router-dom";
import axios from"axios";
import API_BASE_URL from"../Config/config";
import { UserContext } from "./UserContext";
import {
 Box,
 Typography,
 TextField,
 Button,
 Link,
 IconButton,
 InputAdornment,
 Alert,
 Grid,
 Paper,
} from"@mui/material";
import { Visibility, VisibilityOff } from"@mui/icons-material";

 const FormalLogin = () => {
 const navigate = useNavigate();
 const { setUser } = useContext(UserContext);

 const [usernameOrEmail, setUsernameOrEmail] = useState("");
 const [password, setPassword] = useState("");
 const [showPassword, setShowPassword] = useState(false);
 const [error, setError] = useState("");

 useEffect(() => {
   const user = JSON.parse(localStorage.getItem("user"));
   if (user) {
     navigate(`/${user.username || 'dashboard'}`);
   }
 }, [navigate]);

 const handleClickShowPassword = () => {
 setShowPassword((prev) => !prev);
 };

 const handleLogin = async (e) => {
 e.preventDefault();
 setError("");

 try {
 const res = await axios.post(`${API_BASE_URL}/colleges/login`, {
 userId: usernameOrEmail,
 password,
 });

 if (res.data && res.data.user) {
 localStorage.setItem("user", JSON.stringify(res.data.user));
 // Cache College Challenge eligibility for the session — avoids per-page DB queries.
 localStorage.setItem(
   "collegeChallengeEnabled",
   res.data.collegeChallengeEnabled === true ? "true" : "false"
 );
 setUser(res.data.user);
 navigate(`/${res.data.user.username}`);
 } else {
 setError(res.data?.error ||"Login failed. Please try again.");
 }
 } catch (err) {
 console.error(err);
 setError(err.response?.data?.error ||"Login failed. Please try again.");
 }
 };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        backgroundColor: "var(--bg-main)",
        px: 2,
      }}
    >
      <Grid item xs={12} sm={8} md={5}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: 6,
            backgroundColor: "#FFFFFF",
            boxShadow: "0 20px 50px rgba(31, 138, 203, 0.15)",
            color: "var(--text-body)",
            border: "1px solid var(--accent-gray)",
            position: "relative",
            overflow: "hidden"
          }}
        >
          <Box sx={{ position: "absolute", top: -100, right: -100, width: 250, height: 250, borderRadius: "50%", background: "rgba(43, 176, 230, 0.05)", filter: "blur(60px)" }} />
          
          <Typography variant="h4" sx={{ color: "var(--text-heading)", fontWeight: 900, mb: 1, textTransform: "uppercase", letterSpacing: "1px" }}>
            College Sign In
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, color: "var(--text-muted)", fontWeight: 500 }}>
            Don’t have an account?{" "}
            <Link href="/collegesignup" sx={{ color: "var(--primary-blue)", fontWeight: "bold", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
              Register institution
            </Link>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, backgroundColor: "rgba(239, 68, 68, 0.1)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.2)", borderRadius: "12px" }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Username or Email"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
            fullWidth
            variant="outlined"
            InputProps={{ sx: { backgroundColor: "var(--bg-main)", color: "var(--text-heading)", borderRadius: 3, fontWeight: 600 } }}
            InputLabelProps={{ sx: { color: "var(--text-muted)", fontWeight: 600 }, shrink: true }}
            sx={{ 
              mb: 3,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--accent-gray)" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--primary-blue)" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--primary-blue)" },
            }}
          />

          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            variant="outlined"
            InputProps={{
              sx: { backgroundColor: "var(--bg-main)", color: "var(--text-heading)", borderRadius: 3, fontWeight: 600 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    edge="end"
                    sx={{
                      color: "var(--text-muted)",
                      "&:hover": { color: "var(--primary-blue)" }
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ sx: { color: "var(--text-muted)", fontWeight: 600 }, shrink: true }}
            sx={{ 
              mb: 3,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--accent-gray)" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--primary-blue)" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--primary-blue)" },
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 4 }}>
            <Link href="/collegeforgotpass" sx={{ color: "var(--text-muted)", fontWeight: "bold", textDecoration: "none", fontSize: "14px", "&:hover": { color: "var(--primary-blue)" } }}>
              Forgot Password?
            </Link>
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            sx={{
              backgroundColor: "var(--primary-blue)",
              color: "#fff",
              fontWeight: 800,
              fontSize: "1.1rem",
              py: 2,
              borderRadius: 3,
              textTransform: "uppercase",
              boxShadow: "0 10px 20px rgba(43, 176, 230, 0.3)",
              "&:hover": { backgroundColor: "var(--secondary-blue)", transform: "translateY(-1px)", boxShadow: "0 12px 25px rgba(31, 138, 203, 0.4)" },
              transition: "all 0.2s"
            }}
          >
            Login
          </Button>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default FormalLogin;



